import { useFormikValues } from '@hooks/useFormikValues';
import { Field, useFormikContext } from 'formik';
import { AppointmentFormik, appointmentFormValidation } from './types';
import { SubFormProps } from '@forms/Components/types';
import { FormikDatePicker } from '@forms/Components/DatePicker';
import { FormikTimePicker } from '@forms/Components/TimePicker';
import { FormikDateTimePicker } from '@forms/Components/DateTimePicker';
import withSubForm from '@forms/Components/withSubForm';
import { FormikButtonDropdown } from '@forms/Components/Dropdowns/ButtonDropdown';
import LocationButtonContent from './LocationButtonContent';
import LocationListContent from './LocationListContent';
import PhoneLink, { formatPhoneNumber } from '@components/PhoneLink';

import styles from './Form.module.css';

/**
 *  Appointment form component detailing the Scheduled time, Actual time, and Location of an appointment
 */
const AppointmentForm = (props: SubFormProps) => {
	useFormikValues(props);
	const formik = useFormikContext<AppointmentFormik>();

	return (
		<div className={props.fieldProps.className}>
			<fieldset
				style={{ minWidth: '0' }}
				className={styles.fieldset}
			>
				<legend className={styles.legend}>{props.fieldProps.label}</legend>

				<div className={styles.container}>
					<div className={styles['flex-row']}>
						<Field
							name='scheduledDate'
							component={FormikDatePicker}
							label='Scheduled'
							className={styles['date-input']}
						/>

						<Field
							name='scheduledFromTime'
							component={FormikTimePicker}
							label='From'
							className={styles['time-input']}
						/>

						<Field
							name='scheduledToTime'
							component={FormikTimePicker}
							label='To'
							className={styles['time-input']}
						/>
					</div>

					<div>
						<Field
							name='actualDateTime'
							component={FormikDateTimePicker}
							label='Actual'
							className={styles['date-time-input']}
							nowBtn
						/>
					</div>

					<Field
						name='location'
						className={styles['loc-dropdown']}
						component={FormikButtonDropdown({
							ButtonContent: LocationButtonContent,
							buttonContentProps: {
								location: formik.values.location,
								emptyValueText: 'No Location Assigned'
							},
							PopupContent: LocationListContent,
							popupContentProps: {
								legLocation: formik.values.location
							}
						})}
						clearable
					/>

					{props.fieldProps.showLocationDetails && (
						<div
							className={styles['location-details']}
							key={formik.values.location?.id ?? 'no-location'}
						>
							<span className={styles['address-lbl']}>Address</span>
							<span className={styles['address1']}>{formik.values.location?.addr1 ?? '-'}</span>
							<span className={styles['address2']}>
								{formik.values.location
									? `${
											formik.values.location.city && formik.values.location.state
												? `${formik.values.location.city}, ${formik.values.location.state}`
												: `${formik.values.location.city ?? ''}${formik.values.location.state ?? ''}`
										} ${formik.values.location.zip ?? ''}`
									: ''}
							</span>

							<span className={styles['area-lbl']}>Area</span>
							<span className={styles['area1']}>{formik.values.location?.areaName ?? '-'}</span>
							<span className={styles['area2']}>{formik.values.location?.cityId}</span>

							<span className={styles['phone-lbl']}>Phone</span>
							{formik.values.location?.phone ? (
								<PhoneLink
									phoneNumber={formik.values.location.phone}
									className={styles['phone']}
								/>
							) : (
								<span className={styles['phone']}>-</span>
							)}

							<span className={styles['fax-lbl']}>Fax</span>
							<span className={styles['fax']}>{formatPhoneNumber(formik.values.location?.fax) ?? '-'}</span>

							<span className={styles['email-lbl']}>Email</span>
							<span className={styles['email']}>{formik.values.location?.emailAddr ?? '-'}</span>

							<span className={styles['notes-lbl']}>Notes</span>
							<span className={styles['notes']}>{formik.values.location?.notes ?? '-'}</span>

							{props.fieldProps.usePickupNotes && (
								<>
									<span className={styles['pudl-notes-lbl']}>Pickup Notes</span>
									<span className={styles['pudl-notes']}>{formik.values.location?.puNotes ?? '-'}</span>
								</>
							)}

							{props.fieldProps.useDeliveryNotes && (
								<>
									<span className={styles['pudl-notes-lbl']}>Delivery Notes</span>
									<span className={styles['pudl-notes']}>{formik.values.location?.delvNotes ?? '-'}</span>
								</>
							)}
						</div>
					)}
				</div>
			</fieldset>
		</div>
	);
};

export const AppointmentSubform = withSubForm(AppointmentForm, appointmentFormValidation);
